/** @jsxImportSource @emotion/react */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import IconTileRadioButton from '@IconTileRadioButton';
import ListLayout from '@ListLayout';
import RadioButton from '@RadioButton';
import RadioGroup from '@RadioGroup';
import TileLayout from '@TileLayout';
import { useEffect, useReducer, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getFirstEditableComponentOnPage } from '../../Helpers/components';
import {
  clearConstraintsValidations,
  clearSelectedOptionsAndValue,
  selectOptionAndUpdateValue,
  setComponentValidationStatus,
  setIsCheckedByDefault,
  updateOtherValueAndValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';
import QuestionText from '../QuestionText/QuestionText';
import {
  isIconForEveryOption,
  swapS3DomainWithPublicAssetsDomain,
  getIconHeight,
} from '../__helpers__/component.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import useGetOptionHeights from '../../Hooks/useGetOptionHeights/useGetOptionHeights';
import makeRadioInputStyles from './makeRadioInputStyles';
import { ComponentTypesToReceiveFocusOnPageEnter } from '../../Data/Components';

const RadioInput = ({
  autoAdvance,
  componentId,
  subheading,
  iFrameId,
  formId,
}) => {
  const dispatch = useDispatch();
  const [selectionCount, triggerSelection] = useReducer((x) => x + 1, 0);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);
  const supplierMatches = useSelector((store) => store.supplierMatches);
  const showBrandMatchCount = useSelector(
    (store) => store.features.showBrandMatchCount
  );
  const { isIos, isAndroid } = useSelector((store) => store.formConfig);
  const [s3EnvDomain, setS3EnvDomain] = useState(
    process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL
  );
  const { options, hiddenInChameleon, otherValue } = component.input;
  const { text, forceListLayout } = component.component;
  const allOptionsHaveIcons = isIconForEveryOption(options);
  const isTiled = allOptionsHaveIcons && !forceListLayout;
  const theme = useTheme();
  const styles = makeRadioInputStyles(theme);
  let { selectedOptions } = component.input;
  const Layout = isTiled ? TileLayout : ListLayout;

  const { tallestOptionHeight, optionRefs } = useGetOptionHeights({
    options,
    allOptionsHaveIcons,
    componentPage: component?.component?.page,
    isTiled,
  });

  const radioButtonExperimentName = useGetExperimentName({
    componentId,
    componentName: 'RadioButton',
  });
  const iconTileRadioButtonExperimentName = useGetExperimentName({
    componentId,
    componentName: 'IconTileRadioButton',
  });
  const radioGroupExperimentName = useGetExperimentName({
    componentId,
    componentName: 'RadioGroup',
  });
  const layoutExperimentName = useGetExperimentName({
    componentId,
    componentName: isTiled ? 'TileLayout' : 'ListLayout',
  });

  const handleInputChange = ({ id, value }) => {
    batch(() => {
      dispatch(
        clearConstraintsValidations({ componentId: component.component.id })
      );
      dispatch(
        updateOtherValueAndValue({
          componentId,
          optionId: id,
          value,
        })
      );
    });
  };

  const handleSelection = ({ optionId }) => {
    batch(() => {
      dispatch(
        clearSelectedOptionsAndValue({
          componentId,
        })
      );
      dispatch(
        clearConstraintsValidations({ componentId: component.component.id })
      );
      dispatch(
        selectOptionAndUpdateValue({
          componentId,
          optionId,
        })
      );
      if (optionId !== 'other-option') {
        triggerSelection();
        batch(() => {
          dispatch(
            setComponentValidationStatus({
              componentId,
              iFrameId,
              formId,
            })
          );
          dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        });
      } else if (optionId === 'other-option' && otherValue) {
        batch(() => {
          dispatch(
            setComponentValidationStatus({
              componentId,
              iFrameId,
              formId,
            })
          );
          dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        });
      }
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      const firstEditableComponentOnNextPage = getFirstEditableComponentOnPage(
        component.component.page + 1,
        components,
        pages
      );

      if (
        (isIos || isAndroid) &&
        ComponentTypesToReceiveFocusOnPageEnter.includes(
          firstEditableComponentOnNextPage?.input?.type
        )
      ) {
        dispatch(
          setComponentIdToReceiveFocus({
            componentIdToReceiveFocus:
              firstEditableComponentOnNextPage.component.id,
          })
        );
      }

      triggerSelection();
    }
  };

  useEffect(() => {
    options.forEach((option) => {
      if (option.is_checked_by_default) {
        dispatch(
          selectOptionAndUpdateValue({
            componentId,
            optionId: option.id,
          })
        );
        // Remove the default checked status of this option for the lifetime of the form.
        dispatch(
          setIsCheckedByDefault({
            componentId,
            isCheckedByDefault: false,
            optionId: option.id,
          })
        );
      }
    });
  }, []);

  selectedOptions = selectedOptions || [];

  useEffect(() => {
    if (selectedOptions.length > 0 && selectionCount > 0 && autoAdvance) {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [selectionCount]);

  useEffect(() => {
    if (s3EnvDomain !== process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL) {
      setS3EnvDomain(process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL);
    }
  });

  if (hiddenInChameleon === true) {
    return null;
  }

  const optionsWithoutTextLinks = options.filter(
    (option) => !option.display_as_text_link
  );

  const textLinkOption = options.find((option) => option.display_as_text_link);

  const selectedOption = String(selectedOptions[0]);
  const columnCount = component?.component?.additionalSettings?.column_count;

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
          supplierMatches,
          showBrandMatchCount,
        })}
        subheading={renderMustache({
          template: subheading,
          components,
          currentComponent: component,
          iFrameId,
          formId,
          supplierMatches,
          showBrandMatchCount,
        })}
      />
      <Box css={styles.radioContainer} data-testid="radioContainer">
        <RadioGroup
          defaultSelectedId={selectedOption}
          onChange={(optionId) => {
            handleSelection({ optionId });
          }}
          experimentName={radioGroupExperimentName}
        >
          <Layout
            experimentName={layoutExperimentName}
            columns={isTiled ? undefined : columnCount}
          >
            {optionsWithoutTextLinks.map((option) => {
              const { value, key, id, subtext } = option;
              const optionIdString = String(id);
              const isOtherOption = id === 'other-option';
              const optionText = isOtherOption ? key : value;
              const optionSubtext = isOtherOption
                ? component?.component?.additionalSettings?.other_option_subtext
                : subtext;

              const strippedUrl = swapS3DomainWithPublicAssetsDomain(
                option.icon_url,
                process.env.NEXT_PUBLIC_ASSETS_URL
              );
              const iconHeight = getIconHeight(isTiled, columnCount);
              const Icon = allOptionsHaveIcons ? (
                <img
                  style={{
                    maxWidth: '100%',
                    height: iconHeight,
                  }}
                  src={strippedUrl}
                  alt="optionImage"
                />
              ) : undefined;

              const optionComponent = isTiled ? (
                <IconTileRadioButton
                  Icon={Icon}
                  id={optionIdString}
                  key={optionIdString}
                  text={isOtherOption ? key : value}
                  otherInputValue={isOtherOption ? otherValue : undefined}
                  isOtherOption={isOtherOption}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  experimentName={iconTileRadioButtonExperimentName}
                  subtext={optionSubtext}
                  minHeight={tallestOptionHeight}
                />
              ) : (
                <RadioButton
                  Icon={Icon}
                  id={optionIdString}
                  key={optionIdString}
                  text={optionText}
                  otherInputValue={isOtherOption ? otherValue : undefined}
                  isOtherOption={isOtherOption}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  experimentName={radioButtonExperimentName}
                  smallIcon={columnCount > 1 && !isTiled}
                  subtext={optionSubtext}
                  minHeight={tallestOptionHeight}
                />
              );

              return (
                <div
                  key={optionIdString}
                  style={{
                    height: '100%',
                  }}
                  ref={(el) => {
                    optionRefs.current[optionIdString] = el;
                  }}
                >
                  {optionComponent}
                </div>
              );
            })}
          </Layout>
        </RadioGroup>
        {textLinkOption && (
          <Box css={styles.textLinkContainer} data-testid="textLinkOption">
            <Link
              component="button"
              underline="always"
              onClick={(event) => {
                event.preventDefault();
                handleSelection({ optionId: String(textLinkOption.id) });
                triggerSelection();
              }}
              css={styles.textLink}
              onKeyDown={handleKeyPress}
            >
              {textLinkOption.value}
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
};

export default RadioInput;
